
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, TextField, Checkbox, FormControlLabel, Grid,
    CircularProgress, Divider, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import apiUrl from '../../Config';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AddEditSubCategory = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { subService, categories, isEdit } = state || {};

    const [formData, setFormData] = useState({
        Id: subService?.id || 0,
        ServiceId: subService?.categoryId || '',
        Name: subService?.name || '',
        Description: subService?.description || '',
        SequenceOrder: subService?.sequenceOrder || '',
        IsActive: subService?.isActive ?? true,
        IsDeleted: subService?.isDeleted ?? false,
        ImageFile: null,
    });

    const [imagePreview, setImagePreview] = useState(subService?.imageUrl || '');
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState('');

    useEffect(() => {
        if (subService?.imageUrl) setImagePreview(subService.imageUrl);
    }, [subService]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setFieldErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({ ...prev, [name]: checked }));
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFormData((prev) => ({ ...prev, ImageFile: file }));
            setImagePreview(URL.createObjectURL(file));
            setFieldErrors((prev) => ({ ...prev, ImageFile: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let errors = {};

        if (!formData.ServiceId) errors.ServiceId = 'Category is required.';
        if (!formData.Name.trim()) errors.Name = 'Name is required.';
        if (!formData.Description.trim()) errors.Description = 'Description is required.';
        if (!imagePreview && !formData.Id) errors.ImageFile = 'Please upload an image.';
        if (!formData.SequenceOrder || Number(formData.SequenceOrder) <= 0) {
            errors.SequenceOrder = 'Sequence Order must be a positive number.';
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = new FormData();
            payload.append("name", formData.Name);
            payload.append("description", formData.Description);
            payload.append("isActive", formData.IsActive ? "1" : "0");
            payload.append("sequenceOrder", formData.SequenceOrder || "1");
            payload.append("categoryId", formData.ServiceId);
            if (formData.ImageFile) {
                payload.append("subcategoriesImg", formData.ImageFile);
            }

            const res = formData.Id
                ? await axios.put(`${apiUrl}/subcategories/updatesubcategories/${formData.Id}`, payload)
                : await axios.post(`${apiUrl}/subcategories/createsubcategories`, payload);

            if (res.data.status === true) {
                toast.success(formData.Id ? 'Sub-category updated successfully!' : 'Sub-category created successfully!');
                navigate(-1, { state: { refresh: true } });
            } else {
                setError("Something went wrong on server.");
            }
        } catch (error) {
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 4 } }}>
            <Typography variant="h4" fontWeight={400} mb={2} mt={1}>
                {formData.Id ? `Edit Sub Category - ${formData.Name}` : 'Add New Sub Category'}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>

                    {/* Category Dropdown */}
                    <Grid size={{xs:12, sm:6}}>
                        <FormControl size="small" fullWidth error={Boolean(fieldErrors.ServiceId)}>
                            <InputLabel id="category-label">Select Category</InputLabel>
                            <Select
                                labelId="category-label"
                                name="ServiceId"
                                value={formData.ServiceId}
                                label="Select Category"
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        ServiceId: Number(e.target.value),
                                    }))
                                }
                            >
                                <MenuItem value="">
                                    <em>Select a category</em>
                                </MenuItem>
                                {categories?.map((cat) => (
                                    <MenuItem key={cat.id} value={cat.id}>
                                        {cat.name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {fieldErrors.ServiceId && (
                                <Typography variant="caption" color="error">
                                    {fieldErrors.ServiceId}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>

                    {/* Image Upload */}
                    

                    {/* Name */}
                    <Grid size={{xs:12, sm:6}}>
                        <TextField
                            label="Name"
                            name="Name"
                            fullWidth
                            size="small"
                            value={formData.Name}
                            onChange={handleChange}
                            error={Boolean(fieldErrors.Name)}
                            helperText={fieldErrors.Name}
                        />
                    </Grid>

                    {/* Description */}
                    <Grid size={{xs:12, sm:6}}>
                        <TextField
                            label="Description"
                            name="Description"
                            fullWidth
                            size="small"
                            value={formData.Description}
                            onChange={handleChange}
                            error={Boolean(fieldErrors.Description)}
                            helperText={fieldErrors.Description}
                        />
                    </Grid>

                    {/* Sequence Order */}
                    <Grid size={{xs:12, sm:6}}>
                        <TextField
                            label="Sequence Order"
                            name="SequenceOrder"
                            fullWidth
                            size="small"
                            type="number"
                            value={formData.SequenceOrder}
                            onChange={handleChange}
                            error={Boolean(fieldErrors.SequenceOrder)}
                            helperText={fieldErrors.SequenceOrder}
                        />
                    </Grid>

                    <Grid size={{xs:12, sm:6}}>
                        <Typography variant="body1" fontWeight={500} gutterBottom>
                            Sub Category Image
                        </Typography>
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: 2,
                                bgcolor: 'primary.background',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 1,
                            }}
                        >
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    style={{ width: '110px', height: '110px', borderRadius: 4 }}
                                />
                            ) : (
                                <Typography variant="caption">No Image</Typography>
                            )}
                        </Box>
                        <label htmlFor="upload-img">
                            <input
                                type="file"
                                id="upload-img"
                                hidden
                                accept="image/*"
                                onChange={handleImageChange}
                            />
                            <Button variant="outlined" component="span" type="button">
                                Upload Image
                            </Button>
                        </label>
                        {fieldErrors.ImageFile && (
                            <Typography variant="caption" color="error">
                                {fieldErrors.ImageFile}
                            </Typography>
                        )}
                    </Grid>

                    {/* Checkboxes */}
                    <Grid size={{xs:12, sm:6}} sx={{ display: 'flex', gap: 3 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="IsActive"
                                    checked={formData.IsActive}
                                    onChange={handleCheckboxChange}
                                    size="small"
                                />
                            }
                            label="Is Active"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    name="IsDeleted"
                                    checked={formData.IsDeleted}
                                    onChange={handleCheckboxChange}
                                    size="small"
                                />
                            }
                            label="Is Deleted"
                        />
                    </Grid>

                    {/* General Error */}
                    {error && (
                        <Grid size={{xs:12}}>
                            <Typography color="error">{error}</Typography>
                        </Grid>
                    )}

                    {/* Submit Button */}
                    <Grid size={{xs:12}}>
                        <Box display="flex" justifyContent="end" mt={2} gap={2}>
                            <Button variant="outlined" onClick={() => navigate(-1)}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" disabled={loading}>
                                {loading ? (
                                    <CircularProgress size={24} sx={{ color: '#fff' }} />
                                ) : (
                                    formData.Id ? 'Update' : 'Add'
                                )}
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </form>
        </Box>
    );
};

export default AddEditSubCategory;
